<script setup lang="ts">
import { useFormKitContextById } from "@formkit/vue";
import { useRouteParams } from "@vueuse/router";
import type { UpdateInboundProductRequest } from "mws-catalog-ts-sdk";
import { type Ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import BaseButton from "@/common/components/BaseButton.vue";
import {
  useGetInboundProductByIdQuery,
  useUpdateInboundProductMutation,
} from "@/inbound/api/queries";
import InboundProductForm from "@/inbound/components/InboundProductForm.vue";
import AnonymousLayout from "@/layouts/AnonymousLayout.vue";

definePage({
  meta: {
    layout: AnonymousLayout,
  },
});

const { t } = useI18n();
const inboundProductCreateForm = useFormKitContextById(
  "inboundProductCreateForm",
);

const reportId = useRouteParams("reportId") as Ref<string>;
const productId = useRouteParams("productId") as Ref<string>;
const { data: product, isPending } = useGetInboundProductByIdQuery(
  reportId,
  productId,
);

const { mutateAsync: updateInboundProduct } = useUpdateInboundProductMutation();
const submitForm = () => {
  const node = inboundProductCreateForm.value?.node;
  node?.submit();
};

const onSubmit = async (request: UpdateInboundProductRequest) => {
  await updateInboundProduct({
    reportId: reportId.value,
    productId: productId.value,
    updateInboundProductRequest: request,
  });

  router.go(-1);
};

const router = useRouter();
const onCancel = () => {
  router.go(-1);
};
</script>

<template>
  <div>
    <div class="bg-gray-100 pb-3">
      <div class="p-3 bg-white">
        <p class="font-bold text-lg py-1">
          {{ t("inboundReport.productEdit.title") }}
        </p>
        <p>{{ t("inboundReport.productEdit.info") }}</p>
      </div>
    </div>
    <div class="bg-white">
      <ContentLoader
        v-if="isPending"
        view-box="0 0 200 200"
      />
      <InboundProductForm
        v-if="product"
        id="inboundProductCreateForm"
        mode="Update"
        type="form"
        :value="product"
        :actions="false"
        form-class="pb-28"
        messages-class="mx-3"
        @submit="onSubmit"
      />
    </div>
    <div class="flex flex-col gap-1 fixed bottom-0 bg-white w-full p-3">
      <BaseButton
        variant="primary"
        :label="t('inboundReport.productEdit.saveButton')"
        @click="submitForm"
      />
      <BaseButton
        variant="secondary"
        :label="t('inboundReport.productEdit.cancelButton')"
        @click="onCancel"
      />
    </div>
  </div>
</template>
